import { Col, Collapse, Row } from 'antd';
import { useFormikContext } from 'formik';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type FinanceProductSettings } from '../../../../api/types';
import TranslatedTextAreaField from '../../../../components/fields/TranslatedTextAreaField';
import CollapsibleWrapper from '../../../../components/wrappers/CollapsibleWrapper';
import FormFields from '../../../../themes/admin/Fields/FormFields';
import useSystemSwitchData from '../../../../utilities/useSystemSwitchData';
import useBankOptions from '../shared/useBankOptions';
import useWebCalcSettingOptions from '../shared/useWebCalcSettingOptions';
import CalculationForm from './CalculationForm';

const { Panel } = Collapse;

const colSpan = { lg: 8, xs: 24 };

export type ProductDetailsFormProps = {
    updated?: string;
    offset?: string;
    disabled?: boolean;
};

export type FinanceProductFormValues = FinanceProductSettings;

type MainDetailsFormProps = ProductDetailsFormProps & {
    bankOptions: Array<{ label: string; value: string }>;
    webCalcSettingOptions: Array<{ label: string; value: string }>;
};
const MainDetailsForm = ({
    updated,
    offset,
    bankOptions,
    webCalcSettingOptions,
    disabled = false,
}: MainDetailsFormProps) => {
    const { t } = useTranslation(['financeProductDetails']);
    const { yesNoSwitch } = useSystemSwitchData();

    return (
        <Row gutter={[16, 16]}>
            <Col {...colSpan}>
                <FormFields.InputField
                    {...t('financeProductDetails:fields.name', { returnObjects: true })}
                    disabled={disabled}
                    name="displayName"
                    required
                />
            </Col>
            <Col {...colSpan}>
                <FormFields.TranslatedInputField
                    {...t('financeProductDetails:fields.legalName', { returnObjects: true })}
                    disabled={disabled}
                    name="legalName"
                    required
                />
            </Col>
            <Col {...colSpan}>
                <FormFields.InputField
                    {...t('financeProductDetails:fields.identifier', { returnObjects: true })}
                    disabled={disabled}
                    name="identifier"
                    required
                />
            </Col>
            <Col {...colSpan}>
                <FormFields.InputField
                    {...t('financeProductDetails:fields.bankReferenceIdentifier', { returnObjects: true })}
                    disabled={disabled}
                    name="bankReferenceIdentifier"
                />
            </Col>
            <Col {...colSpan}>
                <FormFields.RangePickerField
                    {...t('financeProductDetails:fields.period', { returnObjects: true })}
                    disabled={disabled}
                    name="period"
                    required
                />
            </Col>
            <Col {...colSpan}>
                <FormFields.SelectField
                    {...t('financeProductDetails:fields.bank', { returnObjects: true })}
                    disabled={!!updated || disabled}
                    name="bankId"
                    options={bankOptions}
                    required
                    showSearch
                />
            </Col>
            {webCalcSettingOptions.length > 1 && (
                <Col {...colSpan}>
                    <FormFields.SelectField
                        {...t('financeProductDetails:fields.webCalcSetting', { returnObjects: true })}
                        disabled={disabled}
                        name="webCalcSettingId"
                        options={webCalcSettingOptions}
                        showSearch
                    />
                </Col>
            )}

            <Col {...colSpan}>
                <FormFields.InputNumberField
                    {...t('financeProductDetails:fields.orderNo', { returnObjects: true })}
                    disabled={disabled}
                    max={100}
                    min={0}
                    name="order"
                    required
                />
            </Col>
            <Col {...colSpan}>
                <TranslatedTextAreaField
                    {...t('financeProductDetails:fields.description', { returnObjects: true })}
                    disabled={disabled}
                    name="description"
                />
            </Col>
            <Col {...colSpan}>
                <FormFields.SwitchField
                    {...t('financeProductDetails:fields.active', { returnObjects: true })}
                    disabled={disabled}
                    name="isActive"
                    {...yesNoSwitch}
                    required
                />
            </Col>
            {updated && (
                <Col {...colSpan}>
                    <FormFields.DisplayFieldWithUTCOffset
                        {...t('common:fields.updated', { returnObjects: true, offset })}
                        value={updated}
                    />
                </Col>
            )}
        </Row>
    );
};

const ProductDetailsForm = ({ updated, offset, disabled = false }: ProductDetailsFormProps) => {
    const { t } = useTranslation(['financeProductDetails']);
    const { availableFinanceProductTypesByBankId, options: bankOptions, moduleIdByBankId } = useBankOptions();
    const {
        values: { bankId, type },
        setFieldValue,
    } = useFormikContext<FinanceProductFormValues>();

    const availableFPType = useMemo(
        () => (bankId ? (availableFinanceProductTypesByBankId?.[bankId] ?? []) : []),
        [availableFinanceProductTypesByBankId, bankId]
    );
    useEffect(() => {
        if (availableFPType.length && !availableFPType.includes(type)) {
            setFieldValue('type', '');
        }
    }, [availableFPType, availableFinanceProductTypesByBankId, setFieldValue, type]);

    const moduleId = bankId ? moduleIdByBankId?.[bankId] : null;
    const webCalcSettingOptions = useWebCalcSettingOptions(moduleId);

    return (
        <CollapsibleWrapper defaultActiveKey={['mainDetails', 'calculation']}>
            <Panel key="mainDetails" header={t('financeProductDetails:subTitles.mainDetails')}>
                <MainDetailsForm
                    bankOptions={bankOptions}
                    disabled={disabled}
                    offset={offset}
                    updated={updated}
                    webCalcSettingOptions={webCalcSettingOptions}
                />
            </Panel>
            <Panel key="calculation" header={t('financeProductDetails:subTitles.calculation')}>
                <CalculationForm availableFPType={availableFPType} disabled={disabled} />
            </Panel>
        </CollapsibleWrapper>
    );
};

export default ProductDetailsForm;
